"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useAuthStore } from "@/stores/auth-store";
import { SPECIES_DEX } from "@/data/species";

interface PartySurgie {
  id: string;
  speciesId: number;
  nickname: string | null;
  level: number;
  xp: number;
  currentHp: number;
  maxHp: number;
  partySlot: number | null;
  status: string;
  species?: (typeof SPECIES_DEX)[0];
  moves?: { id: number; name: string; type: string }[];
}

const TYPE_COLORS: Record<string, string> = {
  sales: "#FF6B9D",
  tech: "#00D4FF",
  finance: "#00C853",
  creative: "#FFD700",
  ops: "#FF8C42",
};

export default function PartyPage() {
  const { user } = useAuthStore();
  const router = useRouter();
  const [party, setParty] = useState<PartySurgie[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [nicknameInput, setNicknameInput] = useState("");

  useEffect(() => {
    if (!user) { router.push("/login"); return; }
    fetch(`/api/party?userId=${user.id}`)
      .then(r => r.json())
      .then(d => { setParty(d.party ?? []); setLoading(false); });
  }, [user, router]);

  async function saveNickname(surgieId: string) {
    await fetch("/api/party", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ surgieId, nickname: nicknameInput || null }),
    });
    setParty(p => p.map(s => s.id === surgieId ? { ...s, nickname: nicknameInput || null } : s));
    setEditingId(null);
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <p className="text-white/50 font-mono text-sm">Loading party...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E27] p-6">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-white font-mono text-2xl font-bold">Your Party</h1>
          <div className="flex gap-3">
            <Link href="/dex" className="text-white/50 font-mono text-sm hover:text-white transition-colors">Dex →</Link>
            <Link href="/game" className="text-white/50 font-mono text-sm hover:text-white transition-colors">← Game</Link>
          </div>
        </div>

        {party.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-white/40 font-mono text-sm">No Surggies yet. Go explore to find some!</p>
            <Link href="/game" className="mt-4 inline-block text-[#FF6B9D] font-mono text-sm hover:underline">Return to game →</Link>
          </div>
        ) : (
          <div className="grid gap-4">
            {party.map(s => {
              const color = TYPE_COLORS[s.species?.type1 ?? "ops"] ?? "#FF6B9D";
              const hpPct = Math.round((s.currentHp / s.maxHp) * 100);
              const barColor = hpPct > 50 ? "#00C853" : hpPct > 25 ? "#F39C12" : "#E74C3C";
              return (
                <div
                  key={s.id}
                  className="rounded-xl border p-4"
                  style={{ borderColor: color + "40", backgroundColor: color + "10" }}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="w-16 h-16 rounded-lg border-2 flex items-center justify-center text-3xl flex-shrink-0"
                      style={{ borderColor: color, backgroundColor: color + "20" }}
                    >
                      {s.species?.sprite ?? "🐾"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {editingId === s.id ? (
                          <div className="flex gap-2 items-center">
                            <input
                              autoFocus
                              value={nicknameInput}
                              onChange={e => setNicknameInput(e.target.value)}
                              onKeyDown={e => { if (e.key === "Enter") saveNickname(s.id); if (e.key === "Escape") setEditingId(null); }}
                              placeholder={s.species?.name ?? "Nickname"}
                              className="bg-black/40 border border-white/20 rounded px-2 py-0.5 text-white font-mono text-sm w-32"
                            />
                            <button onClick={() => saveNickname(s.id)} className="text-[#00C853] font-mono text-xs">Save</button>
                            <button onClick={() => setEditingId(null)} className="text-white/40 font-mono text-xs">Cancel</button>
                          </div>
                        ) : (
                          <>
                            <span className="text-white font-mono font-bold text-sm">
                              {s.nickname ?? s.species?.name ?? `#${s.speciesId}`}
                            </span>
                            {s.nickname && (
                              <span className="text-white/40 font-mono text-xs">({s.species?.name})</span>
                            )}
                            <button
                              onClick={() => { setEditingId(s.id); setNicknameInput(s.nickname ?? ""); }}
                              className="text-white/30 hover:text-white/60 font-mono text-xs ml-1"
                            >
                              ✎
                            </button>
                          </>
                        )}
                      </div>
                      <div className="flex gap-3 text-white/50 font-mono text-xs mb-2">
                        <span>Lv.{s.level}</span>
                        <span style={{ color }}>#{s.species?.type1}</span>
                        {s.species?.type2 && <span style={{ color }}>/#{s.species.type2}</span>}
                        <span>{s.status}</span>
                        {s.partySlot === 0 && <span className="text-[#FFD700]">★ lead</span>}
                      </div>
                      <div className="mb-2">
                        <div className="flex justify-between text-white/40 font-mono text-[10px] mb-0.5">
                          <span>HP</span>
                          <span>{s.currentHp}/{s.maxHp}</span>
                        </div>
                        <div className="h-1.5 bg-black/40 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${hpPct}%`, backgroundColor: barColor }} />
                        </div>
                      </div>
                      {(s.moves ?? []).length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {(s.moves ?? []).map(m => (
                            <span key={m.id} className="bg-black/30 border border-white/10 rounded px-1.5 py-0.5 text-white/50 font-mono text-[10px]">
                              {m.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
